import { createLogger } from "./logger.js";

const logger = createLogger("BehaviorLog");

// 从环境变量读取是否启用行为日志上报（默认启用）
// 在 .env 文件中设置 VITE_ENABLE_BEHAVIOR_LOG=false 来禁用
const ENABLE_BEHAVIOR_LOG = import.meta.env.VITE_ENABLE_BEHAVIOR_LOG !== "false";

const MAX_QUEUE_LEN = 200;
const FLUSH_BATCH_SIZE = 20;

function nowSecStr() {
  return Math.floor(Date.now() / 1000).toString();
}

let memQueue = [];
let flushing = false;
let inited = false;
let authToken = "";

function buildReportUrl() {
  const baseURL = import.meta.env.VITE_APP_API_BASE_URL || "";
  const path = "/api/behaviorLog/report";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return baseURL + path;
}

export function setBehaviorLogAuthToken(token) {
  authToken = token ? String(token) : "";
  // token 就绪后尝试 flush 一次
  flushBehaviorLogs().catch(() => {});
}

async function postReport(item) {
  // 没 token 不上报（接口要求 Authorization）
  if (!authToken) return { success: false, error: "NO_AUTH" };

  const url = buildReportUrl();
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json;charset=UTF-8",
      Authorization: authToken,
    },
    body: JSON.stringify(item),
    // 让页面跳转/关闭时也尽量发出去（普通 flush 也不妨带上）
    keepalive: true,
  });
  if (!res.ok) return { success: false, error: `HTTP ${res.status}` };
  // 接口返回格式可能是 {code,message,data}，也可能无 body；这里尽量兼容
  try {
    const data = await res.json();
    if (data && typeof data === "object" && "code" in data) {
      const ok = data.code === 200 || data.code === "200";
      return { success: ok, error: data.message };
    }
  } catch {
    // ignore json parse error
  }
  return { success: true, error: null };
}

/**
 * 上报关键行为日志（不需要 code）
 * @param {{action: string, description?: string, eventTime?: string|number, type?: number}} param0
 */
export function reportBehaviorLog({ action, description = "", eventTime, type = 0 } = {}) {
  if (!action) return;
  
  // 如果禁用上报，直接返回（不记录队列，不发送请求）
  if (!ENABLE_BEHAVIOR_LOG) return;

  const payload = {
    type: type, // 0: 行为日志, 1: 崩溃日志
    action,
    description,
    eventTime: typeof eventTime === "number" ? String(eventTime) : eventTime || nowSecStr(),
  };

  memQueue.push(payload);
  if (memQueue.length > MAX_QUEUE_LEN) memQueue = memQueue.slice(-MAX_QUEUE_LEN);

  // 不阻塞主流程，异步 flush
  queueMicrotask(() => {
    flushBehaviorLogs().catch(() => {});
  });
}

/**
 * 上报异常日志（会拼接 message/stack，注意不要放敏感信息）
 * @param {unknown} error
 * @param {{action?: string, description?: string}} options
 */
export function reportException(error, options = {}) {
  const action = options.action || "exception";
  const descPrefix = options.description ? `${options.description} | ` : "";

  let message = "";
  let stack = "";

  if (error instanceof Error) {
    message = error.message || String(error);
    stack = error.stack || "";
  } else {
    message = String(error);
  }

  const description = `${descPrefix}${message}${stack ? `\n${stack}` : ""}`.slice(0, 300);
  // 崩溃日志使用 type: 1
  reportBehaviorLog({ action, description, type: 1 });
}

export async function flushBehaviorLogs() {
  if (!ENABLE_BEHAVIOR_LOG) return;
  if (flushing) return;
  if (!authToken) return;
  flushing = true;
  try {
    // 每次最多发一批，避免卡死 UI
    const batch = memQueue.slice(0, FLUSH_BATCH_SIZE);
    for (const item of batch) {
      // 确保日志有 type 字段
      // 如果没有 type，根据 action 判断：包含 error/exception/fail 的默认为崩溃日志(1)，否则为行为日志(0)
      if (typeof item.type !== "number") {
        const action = item.action || "";
        const isErrorAction = /error|exception|fail|crash/i.test(action);
        item.type = isErrorAction ? 1 : 0;
      }
      
      const res = await postReport(item);
      if (!res?.success) {
        // 如果 HTTP status 不是 200，从队列中移除（不重试）
        if (res?.error && res.error.startsWith("HTTP")) {
          memQueue.shift();
        }
        // 其他错误保留在队列中，下次重试
        break;
      }
      memQueue.shift();
    }
  } catch (e) {
    // 网络异常等，留队列下次重试
    logger.warn("flushBehaviorLogs 异常:", e?.message || e);
  } finally {
    flushing = false;
  }
}

function setupUnloadFlush() {
  const sendOnUnload = () => {
    if (!ENABLE_BEHAVIOR_LOG) return;
    if (!memQueue.length) return;
    const last = memQueue[memQueue.length - 1];

    // pagehide/beforeunload 阶段尽量不要做复杂逻辑，只保底发“最后一条关键日志”
    const url = buildReportUrl();

    try {
      // keepalive fetch 可以带 Authorization，优先使用
      if (authToken) {
        fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json;charset=UTF-8",
            Authorization: authToken,
          },
          // body: JSON.stringify(last),
          keepalive: true,
        });
        return;
      }
    } catch {}

    try {
      // 无 token 时，最后兜底用 sendBeacon（不保证服务端会收）
      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(last)], { type: "application/json" });
        navigator.sendBeacon(url, blob);
      }
    } catch {}
  };

  window.addEventListener("pagehide", sendOnUnload);
  window.addEventListener("beforeunload", sendOnUnload);
}

function setupGlobalExceptionCapture() {
  if (!ENABLE_BEHAVIOR_LOG) return;
  
  window.addEventListener("error", (event) => {
    // 资源加载错误的 event.error 可能为空
    if (event?.error) {
      reportException(event.error, { action: "window_error" });
    } else {
      reportBehaviorLog({
        action: "window_error",
        description: `${event?.message || "resource_error"} @ ${event?.filename || ""}:${event?.lineno || ""}:${event?.colno || ""}`.slice(
          0,
          4000
        ),
      });
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportException(event?.reason, { action: "unhandledrejection" });
  });
}

/**
 * 初始化（建议在 main.js 调一次）
 */
export function initBehaviorLog() {
  if (inited) return;
  inited = true;
  setupUnloadFlush();
  setupGlobalExceptionCapture();

  // 页面重新可见时尝试 flush 一次
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      flushBehaviorLogs().catch(() => {});
    }
  });

  // 启动后也尝试 flush 一次
  flushBehaviorLogs().catch(() => {});
}



